<template>
  <el-popover
    v-model="popoverVisible"
    placement="bottom-start"
    width="22rem"
    trigger="click"
    popper-class="vtt-picker-popper"
    :disabled="disabled"
    @show="onPopoverShow"
  >
    <div class="vtt-picker__tree-wrap">
      <el-tree
        ref="treeRef"
        class="vtt-picker__tree"
        :data="options"
        node-key="id"
        highlight-current
        default-expand-all
        :indent="18"
        :expand-on-click-node="false"
        :props="{ children: 'children', label: 'label' }"
        :render-content="renderTreeContent"
        @node-click="onNodeClick"
      />
      <div v-if="!options.length" class="vtt-picker__empty">暂无分组数据</div>
    </div>
    <el-input
      slot="reference"
      :value="displayText"
      readonly
      :placeholder="placeholder"
      :disabled="disabled"
      clearable
      class="vtt-picker__input"
      @clear="onClear"
    >
      <i slot="suffix" class="el-input__icon el-icon-arrow-down" />
    </el-input>
  </el-popover>
</template>

<script>
import { applyElTreeExpandAll } from '@/utils/garageTree'

function stripGroupCountSuffix(text) {
  const s = text != null ? String(text).trim() : ''
  if (!s) return ''
  return s.replace(/\s*\[[^\]]*\]\s*$/, '').trim() || s
}

function buildTeamPathToNode(id, nodes = [], path = []) {
  const sid = String(id)
  for (const n of nodes || []) {
    const next = [...path, n.id]
    if (String(n.id) === sid) return next
    if (n.children && n.children.length) {
      const hit = buildTeamPathToNode(id, n.children, next)
      if (hit.length) return hit
    }
  }
  return []
}

export default {
  name: 'VehicleTeamTreePicker',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: '请选择'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      popoverVisible: false
    }
  },
  computed: {
    displayText() {
      const path = Array.isArray(this.value) ? this.value : []
      if (!path.length) return ''
      const teamId = String(path[path.length - 1])
      const node = this.findTeamNode(teamId)
      const raw = node ? node.teamName || node.label || '' : ''
      return stripGroupCountSuffix(raw)
    }
  },
  watch: {
    value: {
      handler() {
        this.syncTreeCurrentKey()
      },
      deep: true
    },
    options: {
      handler() {
        this.$nextTick(() => {
          applyElTreeExpandAll(this.$refs.treeRef, true)
          this.syncTreeCurrentKey()
        })
      },
      deep: true
    }
  },
  methods: {
    findTeamNode(id, nodes = this.options) {
      const sid = String(id)
      for (const n of nodes || []) {
        if (String(n.id) === sid) return n
        const hit = this.findTeamNode(id, n.children)
        if (hit) return hit
      }
      return null
    },
    renderTreeContent(h, { node, data }) {
      const children = [
        h('i', {
          class: 'vtt-picker-tree__icon vtt-picker-tree__icon--folder el-icon-folder',
          attrs: { 'aria-hidden': 'true' }
        })
      ]
      const name = stripGroupCountSuffix(data.teamName || data.label || node.label || '')
      children.push(h('span', { class: 'vtt-picker-tree__group-name' }, name))
      return h('span', { class: 'vtt-picker-tree__node' }, children)
    },
    emitPath(path) {
      const arr = Array.isArray(path) ? path : []
      this.$emit('input', arr)
      this.$emit('change', arr)
    },
    onNodeClick(data) {
      if (!data || data.id == null) return
      const path = buildTeamPathToNode(data.id, this.options)
      this.emitPath(path)
      this.popoverVisible = false
    },
    onClear() {
      this.emitPath([])
      this.syncTreeCurrentKey()
    },
    syncTreeCurrentKey() {
      this.$nextTick(() => {
        const tree = this.$refs.treeRef
        if (!tree || typeof tree.setCurrentKey !== 'function') return
        const path = Array.isArray(this.value) ? this.value : []
        const key = path.length ? path[path.length - 1] : null
        try {
          tree.setCurrentKey(key)
        } catch (e) {
          /* ignore */
        }
      })
    },
    onPopoverShow() {
      this.$nextTick(() => {
        applyElTreeExpandAll(this.$refs.treeRef, true)
        this.syncTreeCurrentKey()
      })
    }
  }
}
</script>

<style scoped>
.vtt-picker__input {
  width: 100%;
}

.vtt-picker__input >>> .el-input__inner {
  cursor: pointer;
}

.vtt-picker__tree-wrap {
  max-height: 18rem;
  overflow: auto;
}

.vtt-picker__empty {
  padding: 1rem 0;
  text-align: center;
  color: #909399;
  font-size: 0.8125rem;
}

.vtt-picker__tree {
  font-size: 0.875rem;
  background: transparent;
}

.vtt-picker__tree >>> .el-tree-node__content {
  height: 1.75rem;
  line-height: 1.75rem;
  padding-right: 0.25rem;
}

.vtt-picker__tree >>> .el-tree-node.is-current > .el-tree-node__content {
  background-color: #e6f7ff !important;
}

.vtt-picker__tree >>> .el-tree-node__content:hover {
  background-color: #f5f7fa;
}

.vtt-picker__tree >>> .el-tree-node__expand-icon {
  color: #409eff;
  font-size: 0.8125rem;
  margin-right: 0.125rem;
}

.vtt-picker__tree >>> .el-tree-node__expand-icon.is-leaf {
  color: transparent;
  pointer-events: none;
}

.vtt-picker__tree >>> .el-tree-node__children {
  position: relative;
}

.vtt-picker__tree >>> .el-tree-node__children::before {
  content: '';
  position: absolute;
  left: 0.4375rem;
  top: 0;
  bottom: 0.5rem;
  border-left: 1px dotted #c0c4cc;
  pointer-events: none;
}

.vtt-picker__tree >>> .vtt-picker-tree__node {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: calc(100% - 0.5rem);
  overflow: hidden;
  vertical-align: middle;
}

.vtt-picker__tree >>> .vtt-picker-tree__icon--folder {
  color: #e6a23c;
  font-size: 1rem;
  flex-shrink: 0;
}

.vtt-picker__tree >>> .vtt-picker-tree__group-name {
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style>
.vtt-picker-popper {
  padding: 0.5rem;
}
</style>
